import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserRole, UserItem } from '../types/index.ts';
import { auth, googleAuthProvider } from '../lib/firebase.ts';
import { onAuthStateChanged, signInWithPopup, signOut, GoogleAuthProvider } from 'firebase/auth';
import { requestGisGmailToken } from '../lib/gisAuth.ts';

interface AuthContextType {
  currentUser: UserItem;
  currentRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  signInWithGoogle: () => Promise<void>;
  connectGoogleGmail: () => Promise<{ email: string; token: string } | null>;
  disconnectGoogleGmail: () => void;
  getGmailAccessToken: () => string | null;
  gmailAccessToken: string | null;
  isGmailConnected: boolean;
  logout: () => Promise<void>;
  hasPermission: (requiredPerm: string) => boolean;
}

const DEFAULT_USER: UserItem = {
  id: 1,
  uid: 'usr-admin-naeem',
  name: 'Naeem Akhtar',
  email: 'naeembuilder48@gmail.com',
  role: 'ADMIN',
  designation: 'Managing Director & CEO',
  phone: '+92 300 8291045',
  status: 'ACTIVE',
  createdAt: new Date().toISOString(),
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserItem>(DEFAULT_USER);
  const [currentRole, setCurrentRole] = useState<UserRole>('ADMIN');
  const [gmailAccessToken, setGmailAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setCurrentUser((prev) => ({
          ...prev,
          uid: firebaseUser.uid,
          email: firebaseUser.email || prev.email,
          name: firebaseUser.displayName || prev.name,
        }));
      } else {
        setGmailAccessToken(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const connectGoogleGmail = async (): Promise<{ email: string; token: string } | null> => {
    try {
      // Use Google Identity Services OAuth 2.0 Token Client (scope: https://www.googleapis.com/auth/gmail.readonly)
      const gisRes = await requestGisGmailToken();
      if (gisRes?.accessToken) {
        setGmailAccessToken(gisRes.accessToken);
        return { email: 'naeembuilder48@gmail.com', token: gisRes.accessToken };
      }
      return null;
    } catch (err: any) {
      console.error('Google Gmail Connect Error:', err);
      throw err;
    }
  };

  const disconnectGoogleGmail = () => {
    setGmailAccessToken(null);
  };

  const getGmailAccessToken = () => gmailAccessToken;

  const signInWithGoogle = async () => {
    try {
      const res = await signInWithPopup(auth, googleAuthProvider);
      const credential = GoogleAuthProvider.credentialFromResult(res);
      if (credential?.accessToken) {
        setGmailAccessToken(credential.accessToken);
      }
      if (res.user) {
        setCurrentUser((prev) => ({
          ...prev,
          uid: res.user.uid,
          email: res.user.email || prev.email,
          name: res.user.displayName || prev.name,
        }));
      }
    } catch (err) {
      console.error('Sign-in error:', err);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(DEFAULT_USER);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const hasPermission = (permissionKey: string): boolean => {
    if (currentRole === 'ADMIN') return true;

    // Site Engineer constraints
    if (currentRole === 'SITE_ENGINEER') {
      const forbiddenForSite = ['accounts', 'consolidated_invoices', 'payroll', 'users', 'roles', 'settings'];
      if (forbiddenForSite.includes(permissionKey)) return false;
      return true;
    }

    // Accounts constraints
    if (currentRole === 'ACCOUNTS') {
      const allowedForAccounts = [
        'dashboard',
        'invoices',
        'consolidated_invoices',
        'accounts',
        'payments',
        'expenses',
        'project_costing',
        'reports',
        'documents',
        'audit_logs',
        'projects',
        'tickets',
      ];
      return allowedForAccounts.includes(permissionKey);
    }

    // Project Manager constraints
    if (currentRole === 'PROJECT_MANAGER') {
      const forbiddenForPM = ['accounts', 'users', 'roles', 'settings', 'payroll'];
      return !forbiddenForPM.includes(permissionKey);
    }

    // Purchase Officer constraints
    if (currentRole === 'PURCHASE_OFFICER') {
      const allowedForPO = ['dashboard', 'purchase', 'vendors', 'inventory', 'documents', 'projects', 'reports'];
      return allowedForPO.includes(permissionKey);
    }

    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentRole,
        setCurrentRole,
        signInWithGoogle,
        connectGoogleGmail,
        disconnectGoogleGmail,
        getGmailAccessToken,
        gmailAccessToken,
        isGmailConnected: Boolean(gmailAccessToken),
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
